import { toSafeString } from '@shared/utils';
const SKILL_TOKEN_PREFIX = '[[skill:';
const FILE_TOKEN_PREFIX = '[[file:';
const MENTION_TOKEN_SUFFIX = ']]';
const MENTION_TOKEN_REGEX = /\[\[(skill|file):([^|\]]+)\|([^|\]]*)\|([^|\]]*)\|([^|\]]*)\]\]/g;
const SKILL_REFERENCES_HEADER = 'Skill references:';
const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\(([^)\n]+)\)/g;

const encodeMentionTokenPart = (value) => {
  return encodeURIComponent(String(value || ''));
};

const decodeMentionTokenPart = (value) => {
  try {
    return decodeURIComponent(String(value || ''));
  } catch {
    return String(value || '');
  }
};

const resolveFileBaseName = (value = '') => {
  const normalized = String(value || '')
    .replace(/\\/g, '/')
    .trim()
    .replace(/\/+$/, '');

  if (!normalized) {
    return '';
  }

  const parts = normalized.split('/');
  return toSafeString(parts[parts.length - 1]);
};

const normalizeMarkdownFileHref = (value = '') => {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return '';
  }

  if (normalized.startsWith('#') || normalized.startsWith('mailto:') || normalized.startsWith('tel:') || normalized.includes('://')) {
    return '';
  }

  const withoutAngleBrackets = normalized.startsWith('<') && normalized.endsWith('>') ? normalized.slice(1, -1) : normalized;

  try {
    return decodeURIComponent(withoutAngleBrackets);
  } catch {
    return withoutAngleBrackets;
  }
};

const toMarkdownFileHref = (value = '') => {
  const normalized = String(value || '').trim();

  if (!normalized) {
    return '';
  }

  return normalized.replace(/%/g, '%25').replace(/\s/g, '%20').replace(/\(/g, '%28').replace(/\)/g, '%29');
};

const normalizeSkillMention = (value = {}) => {
  const skill = value?.skill && typeof value.skill === 'object' ? value.skill : value;
  const skillId = toSafeString(skill?.skillId || skill?.id);
  const title = toSafeString(skill?.meta?.title || skill?.title || skillId);
  const path = toSafeString(skill?.path);
  const entrypoint = toSafeString(skill?.meta?.entrypoint || skill?.entrypoint);

  if (!skillId && !path && !title) {
    return null;
  }

  return {
    skillId: skillId || path || title,
    title,
    path,
    entrypoint
  };
};

const normalizeFileMention = (value = {}) => {
  const filePath = toSafeString(value?.filePath || value?.path || value?.href);
  const title = toSafeString(value?.title || value?.label || value?.name || resolveFileBaseName(filePath)) || 'File';

  if (!filePath && !title) {
    return null;
  }

  return {
    filePath,
    title
  };
};

const toSkillMentionToken = (value = {}) => {
  const normalized = normalizeSkillMention(value);

  if (!normalized) {
    return '';
  }

  return `${SKILL_TOKEN_PREFIX}${encodeMentionTokenPart(normalized.skillId)}|${encodeMentionTokenPart(normalized.title)}|${encodeMentionTokenPart(normalized.path)}|${encodeMentionTokenPart(normalized.entrypoint)}${MENTION_TOKEN_SUFFIX}`;
};

const toFileMentionToken = (value = {}) => {
  const normalized = normalizeFileMention(value);

  if (!normalized || !normalized.filePath) {
    return '';
  }

  return `${FILE_TOKEN_PREFIX}${encodeMentionTokenPart(normalized.filePath)}|${encodeMentionTokenPart(normalized.title)}|${encodeMentionTokenPart(normalized.filePath)}|${encodeMentionTokenPart('')}${MENTION_TOKEN_SUFFIX}`;
};

const parseSkillMentionText = (value = '') => {
  const text = String(value || '');
  const nodes = [];
  let cursor = 0;
  MENTION_TOKEN_REGEX.lastIndex = 0;
  let match = MENTION_TOKEN_REGEX.exec(text);

  while (match) {
    const matchIndex = Number(match.index);

    if (matchIndex > cursor) {
      nodes.push({
        type: 'text',
        text: text.slice(cursor, matchIndex)
      });
    }

    const mentionType = toSafeString(match[1]).toLowerCase();

    if (mentionType === 'skill') {
      nodes.push({
        type: 'skill',
        skillId: decodeMentionTokenPart(match[2]),
        title: decodeMentionTokenPart(match[3]) || 'Skill',
        path: decodeMentionTokenPart(match[4]),
        entrypoint: decodeMentionTokenPart(match[5])
      });
    } else if (mentionType === 'file') {
      const filePath = toSafeString(decodeMentionTokenPart(match[4]) || decodeMentionTokenPart(match[2]));
      const title = toSafeString(decodeMentionTokenPart(match[3]) || resolveFileBaseName(filePath)) || 'File';
      nodes.push({
        type: 'file',
        filePath,
        title
      });
    }

    cursor = matchIndex + match[0].length;
    match = MENTION_TOKEN_REGEX.exec(text);
  }

  if (cursor < text.length) {
    nodes.push({
      type: 'text',
      text: text.slice(cursor)
    });
  }

  if (nodes.length < 1) {
    return [
      {
        type: 'text',
        text
      }
    ];
  }

  return nodes;
};

const mergeAdjacentTextNodes = (nodes = []) => {
  const mergedNodes = [];

  for (const node of nodes) {
    if (node?.type !== 'text') {
      mergedNodes.push(node);
      continue;
    }

    const currentText = String(node?.text || '');
    const previousNode = mergedNodes[mergedNodes.length - 1];

    if (previousNode?.type === 'text') {
      previousNode.text = `${String(previousNode?.text || '')}${currentText}`;
      continue;
    }

    mergedNodes.push({
      type: 'text',
      text: currentText
    });
  }

  return mergedNodes;
};

const parseMarkdownFileLinksIntoNodes = (nodes = []) => {
  const nextNodes = [];

  for (const node of Array.isArray(nodes) ? nodes : []) {
    if (node?.type !== 'text') {
      nextNodes.push(node);
      continue;
    }

    const text = String(node?.text || '');
    let cursor = 0;
    MARKDOWN_LINK_REGEX.lastIndex = 0;
    let match = MARKDOWN_LINK_REGEX.exec(text);

    while (match) {
      const matchIndex = Number(match.index);

      if (matchIndex > cursor) {
        nextNodes.push({
          type: 'text',
          text: text.slice(cursor, matchIndex)
        });
      }

      const title = toSafeString(match[1]);
      const href = normalizeMarkdownFileHref(match[2]);

      if (href) {
        nextNodes.push({
          type: 'file',
          filePath: href,
          title: title || resolveFileBaseName(href) || 'File'
        });
      } else {
        nextNodes.push({
          type: 'text',
          text: match[0]
        });
      }

      cursor = matchIndex + match[0].length;
      match = MARKDOWN_LINK_REGEX.exec(text);
    }

    if (cursor < text.length) {
      nextNodes.push({
        type: 'text',
        text: text.slice(cursor)
      });
    }
  }

  const mergedNodes = mergeAdjacentTextNodes(nextNodes).filter((node) => {
    if (node?.type !== 'text') {
      return true;
    }

    return Object.prototype.hasOwnProperty.call(node, 'text');
  });

  if (mergedNodes.length < 1) {
    return [{ type: 'text', text: '' }];
  }

  return mergedNodes;
};

const serializeSkillMentionNodes = (nodes = []) => {
  if (!Array.isArray(nodes) || nodes.length < 1) {
    return '';
  }

  return nodes
    .map((node) => {
      if (node?.type === 'skill') {
        return toSkillMentionToken(node);
      }

      if (node?.type === 'file') {
        return toFileMentionToken(node);
      }

      return String(node?.text || '');
    })
    .join('');
};

const expandSkillMentionsForPrompt = (value = '') => {
  const parsedNodes = parseSkillMentionText(value);
  const uniqueSkillRefsByKey = new Map();
  let expandedText = '';

  for (const node of parsedNodes) {
    if (node?.type === 'text') {
      expandedText += String(node?.text || '');
      continue;
    }

    if (node?.type === 'file') {
      const normalizedFile = normalizeFileMention(node);

      if (!normalizedFile?.filePath) {
        expandedText += toSafeString(normalizedFile?.title);
        continue;
      }

      const markdownHref = toMarkdownFileHref(normalizedFile.filePath);
      const markdownLabel = toSafeString(normalizedFile.title || resolveFileBaseName(normalizedFile.filePath)) || 'File';
      expandedText += `[${markdownLabel}](${markdownHref || normalizedFile.filePath})`;
      continue;
    }

    const title = toSafeString(node?.title) || 'Skill';
    const skillId = toSafeString(node?.skillId) || title;
    const path = toSafeString(node?.path);
    const entrypoint = toSafeString(node?.entrypoint);
    const refKey = `${skillId}::${path}::${entrypoint}`;

    if (!uniqueSkillRefsByKey.has(refKey)) {
      uniqueSkillRefsByKey.set(refKey, {
        title,
        path,
        entrypoint
      });
    }

    expandedText += `$${title}`;
  }

  const references = Array.from(uniqueSkillRefsByKey.values());

  if (references.length < 1) {
    return expandedText;
  }

  const referenceLines = [SKILL_REFERENCES_HEADER];

  for (const ref of references) {
    const pathText = ref.path ? `path \`${ref.path}\`` : 'path unavailable';
    const entrypointText = ref.entrypoint ? `; entrypoint: ${ref.entrypoint}` : '';
    referenceLines.push(`- $${ref.title}: ${pathText}${entrypointText}`);
  }

  const suffix = referenceLines.join('\n');

  if (!expandedText.trim()) {
    return suffix;
  }

  return `${expandedText}\n\n${suffix}`;
};

const parseSkillReferenceLine = (value = '') => {
  const line = String(value || '').trim();

  if (!line.startsWith('- $')) {
    return null;
  }

  const separatorIndex = line.indexOf(':');

  if (separatorIndex < 0) {
    return null;
  }

  const title = toSafeString(line.slice(3, separatorIndex));
  const detail = toSafeString(line.slice(separatorIndex + 1));

  if (!title || !detail) {
    return null;
  }

  if (detail === 'path unavailable') {
    return {
      title,
      path: '',
      entrypoint: ''
    };
  }

  if (!detail.startsWith('path `')) {
    return null;
  }

  const pathStartIndex = detail.indexOf('`');
  const pathEndIndex = detail.indexOf('`', pathStartIndex + 1);

  if (pathStartIndex < 0 || pathEndIndex <= pathStartIndex + 1) {
    return null;
  }

  const path = toSafeString(detail.slice(pathStartIndex + 1, pathEndIndex));
  const suffix = toSafeString(detail.slice(pathEndIndex + 1));

  if (!path) {
    return null;
  }

  if (!suffix) {
    return {
      title,
      path,
      entrypoint: ''
    };
  }

  if (!suffix.startsWith(';')) {
    return null;
  }

  const normalizedSuffix = toSafeString(suffix.slice(1));
  const entrypointLabel = 'entrypoint:';

  if (!normalizedSuffix.toLowerCase().startsWith(entrypointLabel)) {
    return null;
  }

  return {
    title,
    path,
    entrypoint: toSafeString(normalizedSuffix.slice(entrypointLabel.length))
  };
};

const findTrailingSkillReferences = (value = '') => {
  const text = String(value || '');

  if (!text || !text.includes(SKILL_REFERENCES_HEADER)) {
    return null;
  }

  const lines = text.split('\n');

  for (let index = lines.length - 1; index >= 0; index -= 1) {
    if (toSafeString(lines[index]) !== SKILL_REFERENCES_HEADER) {
      continue;
    }

    const referenceCandidates = lines.slice(index + 1);
    const nonEmptyReferenceLines = referenceCandidates.filter((line) => {
      return toSafeString(line).length > 0;
    });

    if (nonEmptyReferenceLines.length < 1) {
      continue;
    }

    const parsedReferences = [];
    let failedToParse = false;

    for (const referenceLine of nonEmptyReferenceLines) {
      const parsedReference = parseSkillReferenceLine(referenceLine);

      if (!parsedReference) {
        failedToParse = true;
        break;
      }

      parsedReferences.push(parsedReference);
    }

    if (failedToParse || parsedReferences.length < 1) {
      continue;
    }

    return {
      promptText: lines.slice(0, index).join('\n').replace(/\s+$/, ''),
      references: parsedReferences
    };
  }

  return null;
};

const buildSkillNodesFromPromptTextAndReferences = ({ promptText = '', references = [] } = {}) => {
  const text = String(promptText || '');
  const normalizedReferences = [];
  const titleSet = new Set();

  for (const reference of references) {
    const title = toSafeString(reference?.title);

    if (!title || titleSet.has(title)) {
      continue;
    }

    titleSet.add(title);
    normalizedReferences.push({
      title,
      path: toSafeString(reference?.path),
      entrypoint: toSafeString(reference?.entrypoint),
      token: `$${title}`
    });
  }

  normalizedReferences.sort((left, right) => {
    return right.token.length - left.token.length;
  });

  if (normalizedReferences.length < 1) {
    return [{ type: 'text', text }];
  }

  const nodes = [];
  let cursor = 0;

  while (cursor < text.length) {
    let nextMatch = null;

    for (const reference of normalizedReferences) {
      const matchIndex = text.indexOf(reference.token, cursor);

      if (matchIndex < 0) {
        continue;
      }

      if (!nextMatch || matchIndex < nextMatch.index || (matchIndex === nextMatch.index && reference.token.length > nextMatch.reference.token.length)) {
        nextMatch = {
          index: matchIndex,
          reference
        };
      }
    }

    if (!nextMatch) {
      nodes.push({
        type: 'text',
        text: text.slice(cursor)
      });
      break;
    }

    if (nextMatch.index > cursor) {
      nodes.push({
        type: 'text',
        text: text.slice(cursor, nextMatch.index)
      });
    }

    nodes.push({
      type: 'skill',
      skillId: toSafeString(nextMatch.reference.path || nextMatch.reference.title) || nextMatch.reference.title,
      title: nextMatch.reference.title,
      path: nextMatch.reference.path,
      entrypoint: nextMatch.reference.entrypoint
    });
    cursor = nextMatch.index + nextMatch.reference.token.length;
  }

  const mergedNodes = mergeAdjacentTextNodes(nodes).filter((node) => {
    if (node?.type !== 'text') {
      return true;
    }

    return Object.prototype.hasOwnProperty.call(node, 'text');
  });

  if (mergedNodes.length < 1) {
    return [{ type: 'text', text }];
  }

  return mergedNodes;
};

const parseSkillMentionsForDisplay = (value = '') => {
  const text = String(value || '');
  const tokenizedNodes = parseSkillMentionText(text);
  const hasMentionTokens = tokenizedNodes.some((node) => {
    return node?.type === 'skill' || node?.type === 'file';
  });

  if (hasMentionTokens) {
    return {
      text,
      nodes: parseMarkdownFileLinksIntoNodes(tokenizedNodes),
      strippedReferences: false
    };
  }

  const trailingReferences = findTrailingSkillReferences(text);

  if (!trailingReferences) {
    return {
      text,
      nodes: parseMarkdownFileLinksIntoNodes(tokenizedNodes),
      strippedReferences: false
    };
  }

  const promptText = String(trailingReferences?.promptText || '');
  const hasReferenceMentionInPromptText = trailingReferences.references.some((reference) => {
    const title = toSafeString(reference?.title);

    if (!title) {
      return false;
    }

    return promptText.includes(`$${title}`);
  });

  if (!hasReferenceMentionInPromptText && promptText.trim()) {
    return {
      text,
      nodes: parseMarkdownFileLinksIntoNodes(tokenizedNodes),
      strippedReferences: false
    };
  }

  const nodes = buildSkillNodesFromPromptTextAndReferences({
    promptText,
    references: trailingReferences.references
  });

  return {
    text: promptText,
    nodes: parseMarkdownFileLinksIntoNodes(nodes),
    strippedReferences: true
  };
};

export { normalizeSkillMention, normalizeFileMention, parseSkillMentionText, parseSkillMentionsForDisplay, serializeSkillMentionNodes, toSkillMentionToken, toFileMentionToken, expandSkillMentionsForPrompt };
